import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Briefcase, CheckSquare, Users, FileText, DollarSign, 
  LogOut, ClipboardList, Receipt, FileBarChart, Package, ShoppingCart, 
  CalendarDays, Calendar, MoreHorizontal, Bell, ChevronLeft, ChevronRight, ChevronDown, X, CheckCheck, Trash2,
  Library, Settings, UserCog, Sparkles, BarChart3, Store, MessageSquare, FileSpreadsheet, ListTodo, FolderTree, Mail, FolderOpen, UserCircle, Search, IndianRupee, Calculator, ChefHat, ShieldCheck, Activity, Megaphone, BookText, Lock
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { API } from '@/App';
import { useModuleVisibility, usePlanPermissions, useModuleAccess, PATH_MODULE } from '@/hooks/usePlanPermissions';
import TrialBanner from '@/components/TrialBanner';

// iter-386u-ag — global Axios interceptor: turn any 402 insufficient_credits
// error into an "InsufficientCredits" event the header modal can catch. Ensures
// the whole app has one uniform paywall UX no matter which AI tool triggers it.
if (!window.__bcInsufficientCreditsAttached) {
  window.__bcInsufficientCreditsAttached = true;
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 402 && d && typeof d === 'object' && d.error === 'insufficient_credits') {
        window.dispatchEvent(new CustomEvent('bc:insufficient-credits', { detail: d }));
      }
      if (err?.response?.headers?.['x-credits-refunded'] && Number(err.response.headers['x-credits-refunded']) > 0) {
        toast.success('Generation failed. Your credits have been refunded.');
        window.dispatchEvent(new Event('bc:credits-changed'));
      }
      if (err?.response?.status === 402 && d && typeof d === 'object' && d.error === 'plan_required') {
        window.dispatchEvent(new CustomEvent('bc:plan-required', { detail: d }));
      }
      return Promise.reject(err);
    },
  );
}

// showChip=false → global paywall only (mounted in App for standalone pages like Edit BOQ).
export function CreditBalanceChip({ showChip = true, showModal = true }) {
  const [balance, setBalance] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalDetail, setModalDetail] = React.useState(null);
  const [bundles, setBundles] = React.useState([]);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [unlimited, setUnlimited] = React.useState(false);
  const [walletVisible, setWalletVisible] = React.useState(true);

  const refresh = React.useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/billing/my-credits`, { headers: { Authorization: `Bearer ${token}` } });
      // iter-386u-bt — Super-Admin Bypass: honour backend's unlimited flag
      setUnlimited(!!r.data?.unlimited);
      setBalance(r.data?.balance ?? 0);
      setWalletVisible(r.data?.wallet_visible !== false);
    } catch {}
  }, [token]);

  React.useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 60000);
    const handler = async (e) => {
      if (!showModal) return;
      setModalDetail(e.detail || null);
      setModalOpen(true);
      try {
        const r = await axios.get(`${API}/billing/catalog`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        setBundles(r.data?.bundles || []);
      } catch {}
    };
    window.addEventListener('bc:insufficient-credits', handler);
    window.addEventListener('bc:credits-changed', refresh);
    return () => { clearInterval(iv); window.removeEventListener('bc:insufficient-credits', handler); window.removeEventListener('bc:credits-changed', refresh); };
  }, [refresh, token, showModal]);

  if (!showModal && balance === null && !unlimited) return null;
  const isInfinite = unlimited || (balance != null && balance >= 1e8);
  const chipColor = isInfinite ? 'bg-emerald-100 text-emerald-800' : balance < 20 ? 'bg-rose-100 text-rose-800' : 'bg-sky-100 text-sky-800';

  return (
    <>
      {showChip && walletVisible && balance !== null && <button
        onClick={() => navigate('/billing')}
        className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${chipColor} hover:opacity-80 transition`}
        title={isInfinite ? 'Unlimited credits — internal team account' : 'Credit balance — tap to manage'}
        data-testid="credit-balance-chip"
      >
        <IndianRupee size={12} />
        <span>{isInfinite ? 'Unlimited' : `${Number(balance).toLocaleString('en-IN')} credits`}</span>
      </button>}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center p-4" style={{ pointerEvents: 'auto' }} data-testid="insufficient-credits-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-stone-900">Top-up required</h3>
            <p className="text-sm text-stone-600 mt-2">
              {modalDetail?.message || 'You need more credits to run this AI tool.'}
            </p>
            {modalDetail && (
              <div className="mt-3 text-xs text-stone-500">
                Required: <span className="font-semibold text-stone-800">{modalDetail.required} credits</span> · Balance: <span className="font-semibold text-stone-800">{modalDetail.balance}</span>
              </div>
            )}
            {bundles.length > 0 && (
              <div className="mt-4 space-y-2">
                {bundles.map((b) => (
                  <button
                    key={b.code}
                    onClick={() => { setModalOpen(false); buyCreditPack(b); }}
                    className="w-full flex items-center justify-between px-3 py-2 border border-stone-200 rounded hover:border-emerald-400 hover:bg-emerald-50 transition text-sm"
                    data-testid={`topup-bundle-${b.code}`}
                  >
                    <span>{b.pack_name || b.name} · <span className="text-emerald-700 font-semibold">{b.credits.toLocaleString('en-IN')} credits</span></span>
                    <span className="text-stone-900 font-bold">&#8377;{b.amount.toLocaleString('en-IN')}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setModalOpen(false)} className="px-3 py-1.5 text-xs text-stone-500 hover:text-stone-800" data-testid="topup-modal-close">Not now</button>
              <button onClick={() => { setModalOpen(false); navigate('/billing'); }} className="px-4 py-1.5 text-xs bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700" data-testid="topup-modal-go">Buy credits</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
import { useTheme } from '@/contexts/ThemeContext';
import { formatDate } from '@/utils/dateFormat';
import AIChatWidget from '@/components/AIChatWidget';
import { LockedFeaturePanel } from '@/components/billing/PlanUpgradeModal';
import { buyCreditPack } from '@/lib/razorpayCheckout';

const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  const cacheBuster = `?t=${Date.now()}`;
  return logoUrl + cacheBuster;
};

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  // iter-386u-r — persist Projects group expansion across navigation & reloads.
  const [projectsExpanded, setProjectsExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebar.projectsExpanded');
      return stored === null ? false : stored === 'true';
    } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('sidebar.projectsExpanded', String(projectsExpanded)); } catch { /* noop */ }
  }, [projectsExpanded]);
  const [clientSectionExpanded, setClientSectionExpanded] = useState(false);
  const [vendorSectionExpanded, setVendorSectionExpanded] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications?limit=20`);
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    // Fetch notifications on mount
    const loadNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications?limit=20`);
        setNotifications(response.data);
        setUnreadCount(response.data.filter(n => !n.read).length);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    loadNotifications();
  }, []);

  // iter-386u-as — Day-5 in-app trial nudge toast. Fires once per session for
  // trialing users whose trial-nudge email has been sent (i.e. day 5+).
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (!u || u.plan_status !== 'trialing') return;
      if (!u.plan_trial_ends_at || !u.plan_started_at) return;
      const startedAt = new Date(u.plan_started_at).getTime();
      const endsAt = new Date(u.plan_trial_ends_at).getTime();
      const now = Date.now();
      if (!Number.isFinite(startedAt) || !Number.isFinite(endsAt)) return;
      const daysIn = Math.floor((now - startedAt) / 86400000);
      const msLeft = endsAt - now;
      if (daysIn < 5 || msLeft <= 0) return;
      // Idempotency per session
      const sessionKey = `bc_nudge_shown_${u.id}`;
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, '1');
      const code = u.trial_nudge_code || 'TRIAL20';
      const hrsLeft = Math.round(msLeft / 3600000);
      toast(
        `Your trial ends in ~${hrsLeft}h — use code ${code} for 20% off your first month.`,
        {
          duration: 12000,
          action: { label: 'Upgrade', onClick: () => { window.location.href = '/billing'; } },
        },
      );
    } catch { /* silent */ }
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      // Optimistically update the UI first
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Then make the API call
      await axios.put(`${API}/notifications/${notificationId}/read`);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Revert on error - refetch notifications
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await axios.delete(`${API}/notifications/clear-all`);
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setNotificationsOpen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Project-centric navigation - "Projects" is collapsible with sub-items
  // IMPORTANT: Vendors should NOT see Tasks, Schedule, BOQ - only Activities
  // Admin: Tasks page now has "Timeline View" toggle, so Schedule is merged there
  const projectSubItems = [
    { path: '/projects', icon: Briefcase, label: 'All Projects', roles: ['owner', 'admin', 'staff', 'client', 'vendor', 'labour'] },
    { path: '/tasks', icon: CheckSquare, label: 'Tasks & Schedule', roles: ['owner', 'admin', 'staff', 'labour'], capability: 'can_view_tasks' },  // Merged Tasks + Schedule for Admin
    // iter-386u-d — Vendor Tasks & Schedule moved into Projects group so it sits below "All Projects".
    { path: '/vendor/schedule', icon: CalendarDays, label: 'Tasks & Schedule', roles: ['vendor'] },
    // iter-386u-e — Client Tasks & Schedule also moved into Projects group (parity with vendor sidebar reorg).
    { path: '/client/tasks', icon: ListTodo, label: 'Tasks & Schedule', roles: ['client'] },
    { path: '/calendar', icon: Calendar, label: 'Calendar', roles: ['owner', 'admin', 'staff', 'labour', 'vendor', 'client'], capability: 'can_view_calendar' },  // Full Calendar page - ALL ROLES
    { path: '/gantt', icon: BarChart3, label: 'Gantt Chart', roles: ['owner', 'admin', 'staff', 'vendor', 'client'], capability: 'can_view_gantt' },  // P1: Gantt Chart with dependencies
    // iter-386u-d — Vendor My BOQ moved into Projects group so it sits below "Gantt Chart".
    { path: '/vendor/boq', icon: FileSpreadsheet, label: 'My BOQ', roles: ['vendor'] },
    // iter-386u-e — Client BOQ also moved into Projects group.
    { path: '/client/boq', icon: FileSpreadsheet, label: 'My BOQ', roles: ['client'] },
    { path: '/admin/activities', icon: CheckSquare, label: 'Vendor Activities', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendor_activities' },  // Admin manages vendor activities
    { path: '/activities', icon: CheckSquare, label: 'Activities', roles: ['vendor'] },  // Vendor sees their activities
    // iter-386u-t — dedicated Vendor Project Ledger link (below Activities).
    { path: '/vendor/project-ledger', icon: BookText, label: 'Project Ledger', roles: ['vendor', 'labour'], resolveVendorLedger: true },
    // iter-386u-w — Running Bills tab (below Project Ledger).
    { path: '/vendor/running-bills', icon: Receipt, label: 'Running Bills', roles: ['vendor', 'labour'] },
    { path: '/boq-worksheets', icon: ClipboardList, label: 'BOQ', roles: ['owner', 'admin', 'staff'], capability: 'can_view_boq' },  // Internal only
    { path: '/running-bills', icon: Receipt, label: 'Running Bills', roles: ['owner', 'admin', 'staff'], capability: 'can_view_running_bills' },  // iter-326c: RA bills from progress
    { path: '/quotations', icon: FileBarChart, label: 'Quotations', roles: ['owner', 'admin', 'staff'], capability: 'can_view_quotations' },  // Quotation management
    { path: '/project-accounts', icon: IndianRupee, label: 'Project Accounts', roles: ['owner', 'admin', 'staff'], capability: 'can_view_project_accounts' },  // Project account summary
  ];

  // CLIENT SECTION - Separate dropdown for client-related items (Admin view)
  const clientSectionItems = [
    { path: '/clients', icon: UserCircle, label: 'All Clients', roles: ['owner', 'admin', 'staff'] , capability: 'can_view_clients' },
    { path: '/clients-directory', icon: Users, label: 'Client Directory', roles: ['owner', 'admin', 'staff'] , capability: 'can_view_clients' },
    { path: '/client/quotations-sent', icon: FileBarChart, label: 'Quotations Sent', roles: ['owner', 'admin', 'staff'] , capability: 'can_view_clients' },
    { path: '/invoices', icon: Receipt, label: 'Invoices', roles: ['owner', 'admin', 'staff'] , capability: 'can_view_clients' },
    { path: '/client/documents', icon: FileText, label: 'Client Documents', roles: ['owner', 'admin', 'staff'] , capability: 'can_view_clients' },
    { path: '/client/invoices', icon: Receipt, label: 'Client Invoices', roles: ['owner', 'admin', 'staff'] , capability: 'can_view_clients' },
    { path: '/client/payment-history', icon: DollarSign, label: 'Payment Ledger', roles: ['owner', 'admin', 'staff'] , capability: 'can_view_clients' },
  ];

  // VENDOR SECTION - Separate dropdown for vendor-related items (Admin view)
  const vendorSectionItems = [
    { path: '/vendors-labours', icon: Briefcase, label: 'All Vendors & Labour', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
    { path: '/vendor/quotations-received', icon: FileBarChart, label: 'Quotes & Submissions', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
    { path: '/vendor/running-bills', icon: Receipt, label: 'Vendor Running Bills', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
    { path: '/vendor-catalogues', icon: FolderOpen, label: 'Vendor Catalogues', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
    { path: '/vendor/documents', icon: FileText, label: 'Vendor Documents', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
    { path: '/admin/vendor-rates', icon: Store, label: 'Vendor Rates', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
    { path: '/vendor/payment-history', icon: DollarSign, label: 'Payment Ledger', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
    { path: '/vendors', icon: Users, label: 'Vendor Directory', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
    { path: '/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders', roles: ['owner', 'admin', 'staff'], capability: 'can_view_vendors' },
  ];

  // Top-level navigation items (non-collapsible)
  const topNavItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['owner', 'admin', 'staff', 'client', 'vendor', 'labour'] },
    { path: '/leads', icon: Users, label: 'Leads', roles: ['owner', 'admin', 'staff'], capability: 'can_view_leads' },
    { path: '/ai-budget', icon: Sparkles, label: 'AI Assistant', roles: ['owner', 'admin', 'staff', 'project_manager', 'client'] },
    { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['owner', 'admin', 'staff', 'project_manager'], capability: 'can_view_reports' },
  ];

  // Bottom navigation items (below Projects)
  // iter-386u-aq — items tagged with `group` (Operations / Studio Tools /
  // Intelligence). The render walks group-by-group and prints a subtle header
  // above each cluster. Items without a group render at the top (vendor-role
  // items etc, so vendor workflow stays uninterrupted).
  // iter-386u-av — items tagged with `capability` are auto-gated by the
  // Universal Feature Registry via usePlanPermissions(). Owners always see
  // everything (their plan_code resolves to "enterprise" server-side).
  const bottomNavItems = [
    { path: '/estimators/wardrobe', icon: Calculator, label: 'Storage Estimator (Beta)', roles: ['owner', 'admin', 'staff'], newTab: true, moduleKey: 'estimators_storage', group: 'Studio Tools', capability: 'can_use_estimators' },
    { path: '/estimators/kitchen', icon: ChefHat, label: 'Kitchen Estimator (Beta)', roles: ['owner', 'admin', 'staff'], newTab: true, moduleKey: 'estimators_kitchen', group: 'Studio Tools', capability: 'can_use_estimators' },
    { path: '/vendor/rates', icon: Store, label: 'Submit Rates', roles: ['vendor'] },
    { path: '/vendor-submissions', icon: FileBarChart, label: 'My Submissions', roles: ['vendor'] },
    { path: '/vendor-catalogues', icon: FolderOpen, label: 'My Catalogues', roles: ['vendor'] },
    { path: '/vendor/chats', icon: MessageSquare, label: 'Chat with Admin', roles: ['vendor'] },
    { path: '/products', icon: Package, label: 'Products', roles: ['owner', 'admin', 'staff'], group: 'Studio Tools', capability: 'can_view_products' },
    { path: '/materials', icon: Library, label: 'Materials', roles: ['owner', 'admin', 'staff'], group: 'Studio Tools', capability: 'can_view_material_library' },
    { path: '/settings/boq-categories', icon: FolderTree, label: 'BOQ Categories', roles: ['owner', 'admin', 'staff'], group: 'Studio Tools', capability: 'can_manage_boq_categories' },
    { path: '/settings/ai-presets', icon: Sparkles, label: 'AI Preset Library', roles: ['owner', 'admin', 'staff'], group: 'Studio Tools', capability: 'can_use_ai_presets' },
    { path: '/documents', icon: FileText, label: 'Documents', roles: ['owner', 'admin', 'staff', 'vendor', 'labour'], group: 'Operations', capability: 'can_view_documents' },
    { path: '/payments', icon: DollarSign, label: 'Payments', roles: ['owner', 'admin', 'staff'], group: 'Operations', capability: 'can_view_payments' },
    { path: '/vendor/payment-history', icon: DollarSign, label: 'Payments', roles: ['vendor', 'labour'] },
    { path: '/users', icon: UserCog, label: 'Users', roles: ['owner', 'admin', 'staff'], group: 'Operations', capability: 'can_view_users' },
    { path: '/settings/notifications', icon: Bell, label: 'Notification Preferences', roles: ['owner', 'admin', 'staff'], group: 'Operations', capability: 'can_view_notification_prefs' },
    { path: '/admin/active-sessions', icon: Activity, label: 'Active Sessions', roles: ['owner', 'admin'], group: 'Operations', capability: 'can_view_active_sessions' },
    { path: '/admin/performance', icon: BarChart3, label: 'Performance', roles: ['owner', 'admin', 'staff'], group: 'Intelligence', capability: 'can_view_performance' },
    { path: '/admin/data-health', icon: ShieldCheck, label: 'Data Health', roles: ['owner', 'admin'], group: 'Intelligence' },
    { path: '/settings/sponsors', icon: Megaphone, label: 'Sponsored Placements', roles: ['owner', 'admin'], group: 'Intelligence', capability: 'can_view_sponsors' },
    { path: '/admin/billing-manager', icon: IndianRupee, label: 'Revenue & Growth', roles: ['owner'], group: 'Intelligence' },
  ];

  // Client-specific navigation (when logged in as client)
  // iter-386u-e — Tasks & Schedule and BOQ moved into the Projects group (see projectSubItems).
  const clientNavItems = [
    { path: '/client/quotations', icon: FileBarChart, label: 'Quotations', roles: ['client'] },
    { path: '/client/approvals', icon: CheckSquare, label: 'Pending Approvals', roles: ['client'] },
    { path: '/invoices', icon: Receipt, label: 'Invoices', roles: ['client'] },
    { path: '/documents', icon: FileText, label: 'Documents', roles: ['client'] },
    // iter-386u-i — Client-facing Payment Ledger tab (mirrors Vendor's payments tab)
    { path: '/client/payment-history', icon: DollarSign, label: 'Payments', roles: ['client'] },
  ];

  // Filter items by user role
  // iter-386u-ao — hide sidebar items whose moduleKey is disabled for the
  // tenant's nature_of_business (e.g. Landscaping hides Moodboard, Storage).
  // iter-386u-av — additionally hide items whose `capability` is not granted
  // by the user's active plan (Universal Feature Registry).
  const { isVisible: isModuleVisible, loaded: modulesLoaded } = useModuleVisibility();
  const { can: hasCapability, loaded: permsLoaded, required_plans: requiredPlans } = usePlanPermissions();
  const _byModule = (item) => {
    if (!modulesLoaded) return true; // don't flash-hide during load
    if (!item.moduleKey) return true;
    return isModuleVisible(item.moduleKey);
  };
  // Plan gating: owner always unlocked; portal roles (client/vendor/labour) aren't SaaS-billed.
  const _capAllowed = (cap) => !cap || !permsLoaded || user.role === 'owner'
    || !['admin', 'staff', 'project_manager', 'designer', 'site_engineer'].includes(user.role) || hasCapability(cap);
  // Locked items stay visible with a lock and route to the upgrade prompt.
  const _byCapability = () => true;
  const _lock = (item) => (_capAllowed(item.capability) ? item : { ...item, locked: true });
  const { allows: moduleAllowed, loaded: accessLoaded } = useModuleAccess();
  const routeLockedCap = [...topNavItems, ...projectSubItems, ...clientSectionItems, ...vendorSectionItems, ...bottomNavItems]
    .find((i) => i.path === location.pathname && i.capability && i.roles.includes(user.role) && !_capAllowed(i.capability))?.capability;
  const ownerOnlyRoute = location.pathname.startsWith('/admin/billing-manager') && user.role !== 'owner';
  const _byAccess = (item, fallback) => !accessLoaded || moduleAllowed(PATH_MODULE[item.path] || fallback);
  const _visible = (item, fallback) => _byModule(item) && _byCapability(item) && _byAccess(item, fallback);
  const filteredTopNavItems = topNavItems.filter((item) => item.roles.includes(user.role) && _visible(item)).map(_lock);
  const filteredProjectSubItems = projectSubItems.filter((item) => item.roles.includes(user.role) && _visible(item)).map(_lock);
  const filteredClientSectionItems = clientSectionItems.filter((item) => item.roles.includes(user.role) && _visible(item, 'clients')).map(_lock);
  const filteredVendorSectionItems = vendorSectionItems.filter((item) => item.roles.includes(user.role) && _visible(item, 'vendors')).map(_lock);
  const filteredBottomNavItems = bottomNavItems.filter((item) => item.roles.includes(user.role) && _visible(item)).map(_lock);
  const filteredClientNavItems = clientNavItems.filter((item) => item.roles.includes(user.role) && _visible(item));
  
  // All items for mobile (flattened) - include client sections for admin
  const allNavItems = [
    ...filteredTopNavItems,
    ...filteredProjectSubItems,
    ...(user.role === 'client' ? filteredClientNavItems : []),
    ...filteredClientSectionItems,
    ...filteredVendorSectionItems,
    ...filteredBottomNavItems,
  ];
  
  // Mobile bottom nav - show first 4 items + more
  const mobileNavItems = allNavItems.slice(0, 4);
  const moreNavItems = allNavItems.slice(4);

  // Check if current path is a project sub-item or in client/vendor sections
  const isProjectSubPath = projectSubItems.some(item => location.pathname === item.path);
  const isClientSectionPath = clientSectionItems.some(item => location.pathname === item.path) || location.pathname.startsWith('/clients-directory');
  const isVendorSectionPath = vendorSectionItems.some(item => location.pathname === item.path);
  
  // Auto-expand sections based on current route (only on first load)
  useEffect(() => {
    if (isProjectSubPath) {
      setProjectsExpanded(true);
    }
    if (isClientSectionPath) {
      setClientSectionExpanded(true);
    }
    if (isVendorSectionPath) {
      setVendorSectionExpanded(true);
    }
  }, [isProjectSubPath, isClientSectionPath, isVendorSectionPath]);
  
  // Auto-expand sections based on navigation - using refs to avoid cascading renders
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(date);
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-16 md:pb-0">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40" data-testid="header">
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-3 md:gap-4">
            {/* Organization Logo and Name */}
            <Link to="/" className="flex items-center gap-3">
              {theme.logo_url && (
                <img
                  src={getLogoUrl(theme.logo_url)}
                  alt={theme.organization_name || 'Logo'}
                  className="h-8 md:h-10 w-auto object-contain max-w-[120px]"
                  data-testid="header-logo"
                  onError={(e) => {
                    console.error('Logo load error:', e.target.src);
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <h1 className="font-playfair text-xl md:text-2xl font-bold text-stone-900">
                {theme.organization_name || 'Bella Casa'}
              </h1>
            </Link>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Quick Search (Ctrl+K) */}
            <button
              onClick={() => {
                window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
              }}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm text-stone-400 bg-stone-100 hover:bg-stone-200 rounded-lg border border-stone-200 transition-colors"
              data-testid="quick-search-trigger"
            >
              <Search size={14} />
              <span>Search...</span>
              <kbd className="text-[10px] font-mono text-stone-400 bg-white px-1 py-0.5 rounded border border-stone-200 ml-2">
                {navigator.platform?.includes('Mac') ? '\u2318' : 'Ctrl'}+K
              </kbd>
            </button>
            {/* iter-386u-ag — Credit Balance Chip (visible on every dashboard) */}
            <CreditBalanceChip showModal={false} />
            {/* Notification Bell */}
            <Sheet open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <SheetTrigger asChild>
                <button
                  className="relative p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-full transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell size={22} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px] p-0">
                <SheetHeader className="px-4 py-3 border-b bg-stone-50">
                  <SheetTitle className="flex items-center justify-between">
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <span className="text-xs font-normal text-stone-500">{unreadCount} unread</span>
                    )}
                  </SheetTitle>
                </SheetHeader>
                
                {/* Action buttons */}
                {notifications.length > 0 && (
                  <div className="px-4 py-2 border-b bg-white flex gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <CheckCheck size={14} />
                        Mark all read
                      </button>
                    )}
                    <button
                      onClick={clearAllNotifications}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={14} />
                      Clear all
                    </button>
                  </div>
                )}
                
                <div className="overflow-y-auto max-h-[calc(100vh-180px)]">
                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-stone-400">
                      <Bell size={40} className="mb-3 opacity-50" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-stone-100">
                      {notifications.map((notif) => (
                        <div
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          className={`p-4 cursor-pointer transition-colors ${
                            notif.read ? 'bg-white hover:bg-stone-50' : 'bg-blue-50/50 hover:bg-blue-50'
                          }`}
                          data-testid={`notification-${notif.id}`}
                        >
                          <div className="flex gap-3">
                            {!notif.read && (
                              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${notif.read ? 'text-stone-700' : 'text-stone-900 font-medium'}`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{notif.message}</p>
                              <p className="text-xs text-stone-400 mt-1">{formatTime(notif.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            {/* Settings button - visible only to Owner/Staff */}
            {['owner', 'staff', 'admin'].includes(user.role) && (
              <Link
                to="/settings"
                className="text-stone-600 hover:text-stone-900 transition-colors p-2 touch-manipulation"
                data-testid="settings-button"
                title="Organization Settings"
              >
                <Settings size={20} />
              </Link>
            )}
            
            {/* Vendor Settings button */}
            {user.role === 'vendor' && (
              <Link
                to="/vendor/settings"
                className="text-stone-600 hover:text-stone-900 transition-colors p-2 touch-manipulation"
                data-testid="vendor-settings-button"
                title="Company Settings"
              >
                <Settings size={20} />
              </Link>
            )}

            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-stone-900" data-testid="user-name">{user.name}</p>
              <p className="text-xs text-stone-500 capitalize" data-testid="user-role">{user.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-stone-600 hover:text-stone-900 transition-colors p-2 touch-manipulation"
              data-testid="logout-button"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="flex app-shell themable-page" style={{ backgroundColor: 'var(--brand-page, transparent)' }} data-testid="app-shell">
        {/* Desktop Sidebar */}
        <aside
          className={`hidden md:block fixed top-[65px] left-0 bottom-0 border-r border-stone-200 z-30 transition-all duration-300 overflow-y-auto ${
            sidebarExpanded ? 'w-64' : 'w-20'
          }`}
          style={{
            backgroundColor: 'var(--brand-sidebar, #ffffff)',
            color: 'rgb(var(--brand-sidebar-fg, 28 28 30))',
          }}
          data-testid="sidebar"
          data-role="sidebar"
        >
          <nav className="p-2 md:p-3 space-y-0.5">
            {/* Top Navigation Items (Dashboard, Leads) */}
            {filteredTopNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.locked ? `/billing?upgrade=${item.capability}` : item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-colors touch-manipulation ${
                    isActive
                      ? 'bg-brand-primary/10 text-stone-800 font-semibold'
                      : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900 font-medium'
                  } ${sidebarExpanded ? '' : 'justify-center'}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  title={item.label}
                >
                  <Icon size={20} strokeWidth={1.5} className={isActive ? 'text-brand-primary' : ''} />
                  {sidebarExpanded && <span className="text-base flex-1">{item.label}</span>}
                      {sidebarExpanded && item.locked && <Lock size={13} className="text-stone-400" data-testid={`nav-lock-${item.path.replace(/\//g, '-')}`} />}
                </Link>
              );
            })}

            {/* Projects Collapsible Section */}
            {filteredProjectSubItems.length > 0 && (
              <div className="pt-2">
                <button
                  onClick={() => setProjectsExpanded(!projectsExpanded)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors touch-manipulation ${
                    isProjectSubPath
                      ? 'bg-brand-primary/10 text-stone-800 font-semibold'
                      : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900 font-medium'
                  } ${sidebarExpanded ? '' : 'justify-center'}`}
                  data-testid="nav-projects-toggle"
                  title="Projects"
                >
                  <Briefcase size={20} strokeWidth={1.5} className={isProjectSubPath ? 'text-brand-primary' : ''} />
                  {sidebarExpanded && (
                    <>
                      <span className="text-base flex-1 text-left">Projects</span>
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform duration-200 ${projectsExpanded ? '' : '-rotate-90'}`}
                      />
                    </>
                  )}
                </button>

                {/* Project Sub-Items */}
                {projectsExpanded && sidebarExpanded && (
                  <div className="ml-4 pl-4 border-l border-stone-200 mt-1 space-y-0.5">
                    {filteredProjectSubItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.locked ? `/billing?upgrade=${item.capability}` : item.path}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors touch-manipulation text-sm ${
                            isActive
                              ? 'bg-brand-primary/10 text-stone-800 font-semibold'
                              : 'text-stone-500 hover:bg-stone-100 hover:text-stone-900 font-medium'
                          }`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          title={item.label}
                        >
                          <Icon size={16} strokeWidth={1.5} className={isActive ? 'text-brand-primary' : ''} />
                          <span className="flex-1">{item.label}</span>
                          {item.locked && <Lock size={12} className="text-stone-400" data-testid={`nav-lock-${item.path.replace(/\//g, '-')}`} />}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* CLIENT SECTION - Collapsible (Admin only) */}
            {filteredClientSectionItems.length > 0 && (
              <div className="pt-2">
                <button
                  onClick={() => setClientSectionExpanded(!clientSectionExpanded)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors touch-manipulation ${
                    isClientSectionPath
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900 font-medium'
                  } ${sidebarExpanded ? '' : 'justify-center'}`}
                  data-testid="nav-client-section-toggle"
                  title="Client"
                >
                  <Users size={20} strokeWidth={1.5} className={isClientSectionPath ? 'text-blue-600' : ''} />
                  {sidebarExpanded && (
                    <>
                      <span className="text-base flex-1 text-left">Client</span>
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform duration-200 ${clientSectionExpanded ? '' : '-rotate-90'}`}
                      />
                    </>
                  )}
                </button>

                {/* Client Sub-Items */}
                {clientSectionExpanded && sidebarExpanded && (
                  <div className="ml-4 pl-4 border-l border-blue-200 mt-1 space-y-0.5">
                    {filteredClientSectionItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.locked ? `/billing?upgrade=${item.capability}` : item.path}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors touch-manipulation text-sm ${
                            isActive
                              ? 'bg-blue-50 text-blue-700 font-semibold'
                              : 'text-stone-500 hover:bg-stone-100 hover:text-stone-900 font-medium'
                          }`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          title={item.label}
                        >
                          <Icon size={16} strokeWidth={1.5} className={isActive ? 'text-blue-600' : ''} />
                          <span className="flex-1">{item.label}</span>
                          {item.locked && <Lock size={12} className="text-stone-400" data-testid={`nav-lock-${item.path.replace(/\//g, '-')}`} />}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* VENDOR SECTION - Collapsible (Admin only) */}
            {filteredVendorSectionItems.length > 0 && (
              <div className="pt-2">
                <button
                  onClick={() => setVendorSectionExpanded(!vendorSectionExpanded)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors touch-manipulation ${
                    isVendorSectionPath
                      ? 'bg-purple-50 text-purple-700 font-semibold'
                      : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900 font-medium'
                  } ${sidebarExpanded ? '' : 'justify-center'}`}
                  data-testid="nav-vendor-section-toggle"
                  title="Vendor"
                >
                  <Store size={20} strokeWidth={1.5} className={isVendorSectionPath ? 'text-purple-600' : ''} />
                  {sidebarExpanded && (
                    <>
                      <span className="text-base flex-1 text-left">Vendor</span>
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform duration-200 ${vendorSectionExpanded ? '' : '-rotate-90'}`}
                      />
                    </>
                  )}
                </button>

                {/* Vendor Sub-Items */}
                {vendorSectionExpanded && sidebarExpanded && (
                  <div className="ml-4 pl-4 border-l border-purple-200 mt-1 space-y-0.5">
                    {filteredVendorSectionItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.locked ? `/billing?upgrade=${item.capability}` : item.path}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors touch-manipulation text-sm ${
                            isActive
                              ? 'bg-purple-50 text-purple-700 font-semibold'
                              : 'text-stone-500 hover:bg-stone-100 hover:text-stone-900 font-medium'
                          }`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          title={item.label}
                        >
                          <Icon size={16} strokeWidth={1.5} className={isActive ? 'text-purple-600' : ''} />
                          <span className="flex-1">{item.label}</span>
                          {item.locked && <Lock size={12} className="text-stone-400" data-testid={`nav-lock-${item.path.replace(/\//g, '-')}`} />}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* CLIENT NAV - For client role only */}
            {filteredClientNavItems.length > 0 && (
              <div className="pt-2 space-y-1">
                {filteredClientNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.locked ? `/billing?upgrade=${item.capability}` : item.path}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-colors touch-manipulation ${
                        isActive
                          ? 'bg-brand-primary/10 text-stone-800 font-semibold'
                          : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900 font-medium'
                      } ${sidebarExpanded ? '' : 'justify-center'}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      title={item.label}
                    >
                      <Icon size={20} strokeWidth={1.5} className={isActive ? 'text-brand-primary' : ''} />
                      {sidebarExpanded && <span className="text-base flex-1">{item.label}</span>}
                      {sidebarExpanded && item.locked && <Lock size={13} className="text-stone-400" data-testid={`nav-lock-${item.path.replace(/\//g, '-')}`} />}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Divider */}
            {sidebarExpanded && <hr className="my-2 border-stone-200" />}

            {/* Bottom Navigation Items — grouped by iter-386u-aq into
                Operations / Studio Tools / Intelligence for owner/admin. */}
            {(() => {
              const GROUP_ORDER = [null, 'Operations', 'Studio Tools', 'Intelligence'];
              const grouped = new Map(GROUP_ORDER.map((g) => [g, []]));
              for (const it of filteredBottomNavItems) {
                const g = GROUP_ORDER.includes(it.group) ? it.group : null;
                grouped.get(g).push(it);
              }
              return GROUP_ORDER.flatMap((group) => {
                const items = grouped.get(group) || [];
                if (!items.length) return [];
                const header = group && sidebarExpanded ? (
                  <div
                    key={`grp-${group}`}
                    className="mt-3 mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-stone-400"
                    data-testid={`sidebar-group-${group.toLowerCase().replace(/\s+/g, '-')}`}
                  >{group}</div>
                ) : null;
                const links = items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const linkProps = item.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};
                  return (
                    <Link
                      key={item.path}
                      to={item.locked ? `/billing?upgrade=${item.capability}` : item.path}
                      {...linkProps}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-colors touch-manipulation ${
                        isActive
                          ? 'bg-brand-primary/10 text-stone-800 font-semibold'
                          : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900 font-medium'
                      } ${sidebarExpanded ? '' : 'justify-center'}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      title={item.label}
                    >
                      <Icon size={20} strokeWidth={1.5} className={isActive ? 'text-brand-primary' : ''} />
                      {sidebarExpanded && <span className="text-base flex-1">{item.label}</span>}
                      {sidebarExpanded && item.locked && <Lock size={13} className="text-stone-400" data-testid={`nav-lock-${item.path.replace(/\//g, '-')}`} />}
                    </Link>
                  );
                });
                return header ? [header, ...links] : links;
              });
            })()}
          </nav>
          
          {/* Sidebar Toggle Button */}
          <button
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            className="absolute -right-3 top-6 w-6 h-6 bg-white border border-stone-200 rounded-full shadow-sm flex items-center justify-center text-stone-500 hover:text-stone-700 hover:bg-stone-50 transition-colors"
            data-testid="sidebar-toggle"
          >
            {sidebarExpanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          </button>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 min-w-0 overflow-x-hidden p-3 sm:p-4 md:p-8 lg:p-12 min-h-[calc(100vh-65px)] pb-24 md:pb-8 ${sidebarExpanded ? 'md:ml-64' : 'md:ml-20'} transition-all duration-300`} data-testid="main-content">
          {/* iter-386u-aq — Free-trial countdown banner (auto-hides for paid plans) */}
          <TrialBanner />
          {ownerOnlyRoute ? (
            <LockedFeaturePanel title="Owner-only area" />
          ) : routeLockedCap ? (
            <LockedFeaturePanel capability={routeLockedCap} requiredPlan={requiredPlans?.[routeLockedCap]} />
          ) : accessLoaded && PATH_MODULE[location.pathname] && !moduleAllowed(PATH_MODULE[location.pathname]) ? (
            <div className="max-w-md mx-auto mt-24 p-8 rounded-3xl bg-white/60 backdrop-blur-xl border border-white/70 shadow-xl text-center" data-testid="module-access-denied">
              <ShieldCheck className="mx-auto mb-3 text-stone-400" size={36} />
              <h2 className="text-lg font-semibold text-stone-900">No access to this module</h2>
              <p className="text-sm text-stone-500 mt-1">Ask the account owner to enable it under Users → Manage Permissions.</p>
            </div>
          ) : children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-[9999] safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around px-2 py-2 pb-safe">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.locked ? `/billing?upgrade=${item.capability}` : item.path}
                className={`flex flex-col items-center gap-1 py-2 px-3 rounded-xl touch-manipulation min-w-[60px] ${
                  isActive
                    ? 'text-brand-primary bg-brand-primary/5'
                    : 'text-stone-500'
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <Icon size={22} strokeWidth={isActive ? 2 : 1.5} />
                <span className="text-[10px] font-medium">{item.label}</span>
              </Link>
            );
          })}
          
          {/* More Menu */}
          {moreNavItems.length > 0 && (
            <Sheet open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
              <SheetTrigger asChild>
                <button 
                  className="flex flex-col items-center gap-1 py-2 px-3 rounded-xl touch-manipulation min-w-[60px] text-stone-500"
                  data-testid="mobile-nav-more"
                >
                  <MoreHorizontal size={22} strokeWidth={1.5} />
                  <span className="text-[10px] font-medium">More</span>
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-3xl z-[10000]">
                <div className="grid grid-cols-4 gap-4 py-6">
                  {moreNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.locked ? `/billing?upgrade=${item.capability}` : item.path}
                        onClick={() => setMoreMenuOpen(false)}
                        className={`flex flex-col items-center gap-2 py-3 px-2 rounded-xl touch-manipulation ${
                          isActive
                            ? 'bg-primary/10 text-stone-800'
                            : 'text-stone-600 hover:bg-stone-100'
                        }`}
                      >
                        <Icon size={24} strokeWidth={1.5} />
                        <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </nav>

      {/* iter-338l: Floating AI Chat Assistant — visible on all authenticated pages */}
      <AIChatWidget />
    </div>
  );
}
